<template>
  <div
    v-if="menu"
    class="abr-sidebar"
    @click.stop="mini = false"
  >
    <v-navigation-drawer
      v-resize="onResize"
      mini-variant-width="56"
      :mini-variant="mini"
      permanent
      height="96vh"
      class="menu-drawer pa-0"
      width="270"
    >
      <!-- Sidebar header -->
      <div
        class="menu-header"
        :class="{ 'menu-header--mini': mini }"
      >
        <div class="menu-header__brand">
          <div class="menu-header__logo-wrap">
            <img
              max-width="48"
              max-height="48"
              src="@/assets/abrLogo.png"
              alt="ABR"
              class="menu-header__logo"
            />
          </div>
          <div
            v-if="show"
            class="menu-header__text"
          >
            <span class="menu-header__company">{{ companyName }}</span>
            <span class="menu-header__app">{{ title }}</span>
          </div>
        </div>

        <v-btn
          v-if="show"
          icon
          small
          class="menu-header__collapse"
          @click.stop="mini = true"
        >
          <v-icon small>
            mdi-chevron-right
          </v-icon>
        </v-btn>
      </div>

      <div class="menu-body">
        <v-list class="menu-list py-2">
          <!-- Group menus (with children) -->
          <v-list-group
            v-for="({ show, action }, index) in menu.filter((m) => m.action.menuItems)"
            v-show="show"
            :key="index"
            link
            no-action
            class="menu-group"
          >
            <template #activator>
              <div class="menu-activator">
                <div class="menu-activator__icon">
                  <v-icon>{{ action.iconName }}</v-icon>
                </div>
                <div class="menu-activator__title">
                  <span>{{ $t(action) }}</span>
                </div>
              </div>
            </template>

            <!-- Child items -->
            <v-list-item
              v-for="child in action.menuItems"
              v-show="show && child.nestedShow"
              :key="child.nestedAction.id"
              link
              :to="child.nestedTarget"
              class="menu-child"
              active-class="menu-child--active"
            >
              <v-list-item-content class="menu-child__content">
                <span class="menu-child__title">{{ $t(child.nestedAction) }}</span>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>

          <!-- Regular menus (without children) -->
          <v-list-item
            v-for="{ show, action, target } in menu.filter((m) => !m.action.menuItems)"
            v-show="show"
            :key="action.id"
            link
            :to="target"
            class="menu-item"
            active-class="menu-item--active"
          >
            <div class="menu-item__icon">
              <v-icon>{{ action.iconName }}</v-icon>
            </div>
            <v-list-item-content class="menu-item__title">
              <span>{{ $t(action) }}</span>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <!-- Dev warning -->
        <div
          v-if="isItDev"
          class="dev-indicator"
          :title="subDomain ? `نسخه تستی : ${envId}` : 'نسخه تستی'"
        >
          <v-icon
            medium
            class="dev-indicator__icon"
          >
            mdi-alert-circle
          </v-icon>

          <span
            v-if="show"
            class="dev-indicator__text"
          >
            {{ subDomain ? `نسخه تستی  ${envId}` : "نسخه تستی" }}
          </span>
        </div>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import { instance } from "@/apis";
import { getCompanyName } from "@/utils/companyBranding";

export default {
  data: () => ({
    app: null,
    menu: [],
    mini: false,
    show: true,
    windowSize: null,
    isItDev: false,
    subDomain: null,
  }),

  computed: {
    title() {
      return this.app ? this.$t(this.app) : "صفحه اصلی";
    },
    compId() {
      try {
        return instance().compId || "day";
      } catch {
        return "day";
      }
    },
    companyName() {
      return getCompanyName(this.compId);
    },
    envId() {
      return instance().envId;
    },
  },

  async created() {
    this.checkDevEnvironment();
    this.setupWatchers();
    this.loadMenu();
  },

  methods: {
    // Checks if current environment is a test domain
    checkDevEnvironment() {
      const domain = /:\/\/([^/]+)/.exec(window.location.href)[1];
      let sub = domain.split(".")[0];
      if (sub.includes("-")) sub = sub.split("-").pop();

      const testDomains = new Set([
        "insider",
        "develop",
        "qc",
        "uat",
        "abruat",
        "abr-test",
        "deftest",
        "testlife",
        "localhost:8082",
      ]);

      const showDomain = {
        "localhost:8082": "لوکال",
      };

      this.isItDev = testDomains.has(sub);
      this.subDomain = showDomain[sub] || null;
    },

    // Sets up watchers for mini state and app changes
    setupWatchers() {
      this.$watch("mini", (val) => (this.show = !val));
      this.$watch("$store.state.app", () => this.loadMenu(), { immediate: true });
    },

    // Loads menu items from store or route
    async loadMenu() {
      if (this.menu.length) return;

      let app = this.$store.state.app || this.$route.params.app;
      this.app = this.$store.state.root?.apps.find((a) => a.name === app);

      const menus = this.app?.menus || [];
      this.menu = await Promise.all(menus.map((item) => this.buildMenuItem(item)));
    },

    // Builds a single menu item with visibility and target
    async buildMenuItem(item) {
      if (item.menuItems) {
        item.menuItems = await Promise.all(
          item.menuItems.map(async (child) => ({
            nestedShow: await this.$checkVisibility(child),
            nestedAction: child,
            nestedTarget: await this.$target(child),
          }))
        );
      }

      return {
        show: await this.$checkVisibility(item),
        action: item,
        target: await this.$target(item),
      };
    },

    // Closes sidebar on mobile viewports
    onResize() {
      if (this.windowSize === window.innerWidth) return;
      this.windowSize = window.innerWidth;
      this.mini = window.innerWidth < 768;
    },
  },
};
</script>

<style lang="scss">
.abr-sidebar {
  .menu-drawer {
    border-radius: 14px;
    margin: 12px;
    overflow: hidden;
    background: var(--abr-BG1) !important;
    border: 1px solid var(--abr-G4);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  }

  // Header
  .menu-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 18px 14px 16px;
    background: linear-gradient(
      135deg,
      var(--abr-gradient-start),
      var(--abr-gradient-end)
    );
    color: #fff;
    min-height: 108px;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, transparent 55%);
      pointer-events: none;
    }

    &--mini {
      flex-direction: column;
      justify-content: center;
      padding: 14px 8px;
      min-height: 72px;
    }
  }

  .menu-header__brand {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    z-index: 1;
  }

  .menu-header--mini .menu-header__brand {
    flex-direction: column;
    gap: 0;
  }

  .menu-header__logo-wrap {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.95);
    padding: 8px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .menu-header--mini .menu-header__logo-wrap {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    padding: 5px;
  }

  .menu-header__logo {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .menu-header__text {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .menu-header__company {
    font-size: 13px;
    font-weight: 600;
    opacity: 0.92;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .menu-header__app {
    font-size: 16px;
    font-weight: 800;
    line-height: 1.3;
    white-space: normal;
  }

  .menu-header__collapse {
    color: #fff !important;
    background: rgba(255, 255, 255, 0.14) !important;

    &:hover {
      background: rgba(255, 255, 255, 0.24) !important;
    }
  }

  // Body
  .menu-body {
    display: flex;
    flex-direction: column;
    height: calc(100% - 108px);
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
      width: 0;
    }
  }

  .menu-header--mini + .menu-body {
    height: calc(100% - 72px);
  }

  .menu-list {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    background: transparent !important;
    padding-bottom: 56px !important;

    &::-webkit-scrollbar {
      width: 0;
      height: 0;
      display: none;
    }
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  // Shared icon styles
  %icon-base {
    width: 34px;
    height: 34px;
    min-width: 34px;
    border-radius: 8px;
    background: var(--abr-P2);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 12px;
    flex-shrink: 0;

    .v-icon {
      color: var(--abr-P1) !important;
      font-size: 18px !important;
      margin: 0 !important;
    }
  }

  %title-base {
    font-size: 14px;
    font-weight: 500;
    color: var(--abr-G1);
    white-space: normal;
    line-height: 1.3;
    word-wrap: break-word;
  }

  %hover-base {
    &:hover {
      background: var(--abr-P2) !important;
    }
  }

  // Group menus
  .menu-group {
    border-radius: 10px;
  }

  .menu-activator {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 8px 12px;
    min-height: 46px;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
    @extend %hover-base;

    &__icon {
      @extend %icon-base;
    }

    &__title {
      flex: 1;
      text-align: right;

      span {
        @extend %title-base;
        display: inline-block;
      }
    }
  }

  // Active group state
  .menu-group.v-list-group--open .menu-activator {
    &__title span {
      color: var(--abr-P1) !important;
      font-weight: 700;
    }

    &__icon {
      background: rgba(255, 255, 255, 0.55);
    }
  }

  // Child items
  .menu-child {
    margin: 2px 0 !important;
    border-radius: 8px;
    min-height: 40px !important;
    transition: all 0.2s ease;
    @extend %hover-base;

    &__content {
      padding: 0 !important;
    }

    &__title {
      font-size: 13px;
      font-weight: 400;
      color: var(--abr-G1);
      white-space: normal;
      line-height: 1.1;
    }

    &--active {
      background: var(--abr-P2) !important;
      position: relative;

      .menu-child__title {
        color: var(--abr-P1) !important;
        font-weight: 700;
      }

      &::after {
        content: "";
        width: 3px;
        height: 70%;
        position: absolute;
        top: 15%;
        right: 0;
        border-radius: 4px 0 0 4px;
        background: linear-gradient(
          180deg,
          var(--abr-gradient-start),
          var(--abr-gradient-end)
        );
      }
    }
  }

  // Regular menu items
  .menu-item {
    display: flex;
    align-items: center;
    margin: 4px 8px !important;
    border-radius: 10px;
    min-height: 46px !important;
    transition: all 0.2s ease;
    @extend %hover-base;

    &__icon {
      @extend %icon-base;
    }

    &__title {
      flex: 1;
      padding: 0 !important;

      span {
        @extend %title-base;
        display: inline-block;
      }
    }

    &--active {
      background: var(--abr-P2) !important;
      position: relative;

      .menu-item__title span {
        color: var(--abr-P1) !important;
        font-weight: 700;
      }

      .menu-item__icon {
        background: rgba(255, 255, 255, 0.55);
      }

      &::after {
        content: "";
        width: 4px;
        height: 70%;
        position: absolute;
        top: 15%;
        right: 0;
        border-radius: 4px 0 0 4px;
        background: linear-gradient(
          180deg,
          var(--abr-gradient-start),
          var(--abr-gradient-end)
        );
      }
    }
  }

  // Dev warning
  .dev-indicator {
    position: absolute;
    bottom: 12px;
    left: 12px;
    right: 12px;

    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    padding: 6px 10px;
    border-radius: 999px;

    background: color-mix(in srgb, var(--abr-P2) 95%, transparent);
    border-bottom: 3px solid rgb(243, 4, 4);

    color: var(--abr-P1);
    font-size: 13px;
    font-weight: 600;

    opacity: 0.9;
  }
  .dev-indicator__icon {
    color: var(--abr-P1);
  }
}

// Mini variant
.v-navigation-drawer--mini-variant {
  .menu-activator {
    justify-content: center;
    padding: 8px;

    &__icon {
      margin-left: 0 !important;
    }

    &__title,
    &__chevron {
      display: none !important;
    }
  }

  .menu-item {
    justify-content: center;
    padding: 8px !important;

    &__icon {
      margin-left: 0 !important;
    }

    &__title {
      display: none !important;
    }
  }

  .menu-child {
    display: none !important;
  }
}
</style>
